import { useState } from "react";
import type { Lesson, ScenarioResult, CourseProgress } from "@/lib/types";
import { lessons } from "@/lib/course-data";
import { LessonView } from "@/components/LessonView";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
	ArrowDown,
	ArrowUpDown,
	Box,
	Cable,
	ChevronRight,
	GraduationCap,
	MoveRight,
	Sparkles,
	Triangle,
} from "lucide-react";
import "./index.css";

const LESSON_ICONS: Record<string, React.ReactNode> = {
	weight: <ArrowDown className="w-5 h-5" />,
	friction: <MoveRight className="w-5 h-5" />,
	ramp: <Triangle className="w-5 h-5" />,
	tension: <Cable className="w-5 h-5" />,
	elevator: <ArrowUpDown className="w-5 h-5" />,
	challenge: <Sparkles className="w-5 h-5" />,
};

function loadProgress(): CourseProgress {
	try {
		const saved = localStorage.getItem("fbd-course-progress");
		if (saved) return JSON.parse(saved);
	} catch {}
	return { currentLessonIndex: 0, currentScenarioIndex: 0, completedScenarios: [], results: [] };
}

function saveProgress(progress: CourseProgress) {
	localStorage.setItem("fbd-course-progress", JSON.stringify(progress));
}

export function App() {
	const [progress, setProgress] = useState<CourseProgress>(loadProgress);
	const [activeLessonId, setActiveLessonId] = useState<string | null>(null);

	const activeLesson = lessons.find((l) => l.id === activeLessonId);

	const totalScenarios = lessons.reduce((sum, l) => sum + l.scenarios.length, 0);
	const completedCount = progress.completedScenarios.length;
	const overallProgress = totalScenarios > 0 ? (completedCount / totalScenarios) * 100 : 0;

	const getLessonProgress = (lesson: Lesson) => {
		const completed = lesson.scenarios.filter((s) => progress.completedScenarios.includes(s.id)).length;
		return { completed, total: lesson.scenarios.length, percent: (completed / lesson.scenarios.length) * 100 };
	};

	const getLessonResults = (lesson: Lesson): ScenarioResult[] => {
		return progress.results.filter((r) => lesson.scenarios.some((s) => s.id === r.scenarioId));
	};

	const handleLessonComplete = (lessonId: string, results: ScenarioResult[]) => {
		const lesson = lessons.find((l) => l.id === lessonId);
		if (!lesson) return;

		const newCompleted = [...progress.completedScenarios];
		const newResults = [...progress.results];

		for (const result of results) {
			if (!newCompleted.includes(result.scenarioId)) {
				newCompleted.push(result.scenarioId);
			}
			const existingIdx = newResults.findIndex((r) => r.scenarioId === result.scenarioId);
			if (existingIdx >= 0) {
				newResults[existingIdx] = result;
			} else {
				newResults.push(result);
			}
		}

		const newProgress = { ...progress, completedScenarios: newCompleted, results: newResults };
		setProgress(newProgress);
		saveProgress(newProgress);
	};

	const handleReset = () => {
		const fresh: CourseProgress = { currentLessonIndex: 0, currentScenarioIndex: 0, completedScenarios: [], results: [] };
		setProgress(fresh);
		saveProgress(fresh);
	};

	if (activeLesson) {
		return (
			<div className="min-h-screen bg-background p-4 sm:p-6">
				<LessonView
					key={activeLesson.id}
					lesson={activeLesson}
					onBack={() => setActiveLessonId(null)}
					onComplete={(results) => handleLessonComplete(activeLesson.id, results)}
					previousResults={getLessonResults(activeLesson)}
				/>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-background">
			{/* Hero */}
			<div className="border-b bg-card">
				<div className="max-w-2xl mx-auto px-4 py-8 sm:py-12 text-center">
					<div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary text-primary-foreground mb-4">
						<GraduationCap className="w-7 h-7" />
					</div>
					<h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Forces & Free-Body Diagrams</h1>
					<p className="text-muted-foreground mt-2 text-sm sm:text-base max-w-md mx-auto">
						Practice constructing vector diagrams to identify all forces acting on objects in various environments.
					</p>

					{/* Overall progress */}
					<div className="mt-6 max-w-xs mx-auto space-y-1.5">
						<div className="flex justify-between text-xs text-muted-foreground">
							<span>Course Progress</span>
							<span>{completedCount} / {totalScenarios} scenarios</span>
						</div>
						<Progress value={overallProgress} className="h-2" />
					</div>
				</div>
			</div>

			{/* Lessons grid */}
			<div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">Lessons</h2>
					{completedCount > 0 && (
						<Button variant="ghost" size="sm" onClick={handleReset} className="text-xs text-muted-foreground">
							Reset Progress
						</Button>
					)}
				</div>

				<div className="grid gap-3">
					{lessons.map((lesson, index) => {
						const lp = getLessonProgress(lesson);
						const isComplete = lp.completed === lp.total;
						const isStarted = lp.completed > 0;

						return (
							<Card
								key={lesson.id}
								className="cursor-pointer hover:border-ring/50 transition-colors group"
								onClick={() => setActiveLessonId(lesson.id)}
							>
								<CardContent className="py-4 px-4 flex items-center gap-4">
									{/* Icon */}
									<div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
										isComplete
											? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
											: isStarted
												? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
												: "bg-muted text-muted-foreground"
									}`}>
										{LESSON_ICONS[lesson.icon] ?? <Box className="w-5 h-5" />}
									</div>

									{/* Content */}
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 mb-0.5">
											<span className="text-xs text-muted-foreground font-medium">
												Lesson {index + 1}
											</span>
											{isComplete && (
												<Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800">
													Complete
												</Badge>
											)}
										</div>
										<h3 className="font-semibold text-sm truncate">{lesson.title}</h3>
										<p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{lesson.description}</p>

										{/* Lesson progress bar */}
										{isStarted && (
											<div className="mt-2">
												<Progress value={lp.percent} className="h-1" />
											</div>
										)}
									</div>

									{/* Arrow */}
									<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
								</CardContent>
							</Card>
						);
					})}
				</div>

				<Separator className="my-4" />

				{/* How it works */}
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm">How It Works</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 text-xs text-muted-foreground">
						<p><strong className="text-foreground">1. Learn concepts</strong> &mdash; Each lesson starts with key physics concepts to review.</p>
						<p><strong className="text-foreground">2. Build diagrams</strong> &mdash; Select force types and directions to construct free-body diagrams on the interactive canvas.</p>
						<p><strong className="text-foreground">3. Check your work</strong> &mdash; Submit your diagram and get instant feedback. Use hints if you're stuck.</p>
						<p><strong className="text-foreground">4. Review explanations</strong> &mdash; Read detailed explanations of why each force is present and how they balance.</p>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

export default App;
